<script setup lang="ts">
import { useImage, AtomIcon, AtomText } from "@chatfood/design-system";
import { bemBuilder } from "@chatfood/core-utils";
import { useDate } from "~/composables/use-date";
import type { IDomainLoyaltyProgram } from "~/domain/loyalty-program";

const css = bemBuilder("loyalty-card");

const props =
  defineProps<
    Pick<IDomainLoyaltyProgram, "title" | "photo" | "cost" | "expiresAt">
  >();

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const { optimize } = useImage();
const { getReadableDate } = useDate();

function getImage(photo?: string | null) {
  if (!photo) return "";
  return `background-image: url(${optimize(photo, { w: 300, h: 300, fit: "crop" })})`;
}
</script>

<template>
  <a href="" :class="css('')" @click.prevent="emits('click', $event)">
    <div data-test="image" :class="css('image')" :style="getImage(props.photo)">
      <AtomIcon
        v-if="!props.photo"
        size="x-large"
        color="color-base-70"
        name="placeholder-photo"
        data-test="placeholder-photo"
      />
    </div>
    <AtomText
      tag="div"
      type="body-bold"
      :class="css('title')"
      color="color-text-body"
    >
      {{ props.title }}
    </AtomText>
    <AtomText
      v-if="typeof props.cost === 'number' && props.cost > 0"
      tag="div"
      data-test="cost"
      :class="css('cost')"
      color="color-text-body"
    >
      <AtomIcon size="small" name="rating" />
      {{ props.cost }}
    </AtomText>
    <AtomText
      v-if="props.expiresAt"
      type="small"
      data-test="expires-at"
      color="color-text-secondary"
    >
      {{ getReadableDate(props.expiresAt, "DD/MM/YYYY") }}
    </AtomText>
  </a>
</template>

<style lang="scss" scoped>
.loyalty-card {
  display: block;

  &__image {
    display: flex;
    aspect-ratio: 1;
    align-items: center;
    justify-content: center;
    background-size: cover;
    background-position: center;
    margin-bottom: var(--spacing-sm);
    border-radius: var(--radii-card-md);
    background-color: var(--color-base-95);
    box-shadow: none;
    transition: all 0.2s ease-out;
  }

  &__title {
    color: var(--text-color-body);
    transition: all 0.2s ease-out;
  }

  &__cost {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }

  &:hover .loyalty-card {
    &__image {
      box-shadow: var(--elevation-level-2);
    }

    &__title {
      color: var(--color-primary) !important;
    }
  }
}
</style>
